import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Pill } from "@/components/common/States";
import { labelOf } from "@/lib/resources";
import { fmtDate, fmtMoney } from "@/lib/api";

function rawValue(col, row, lookups) {
  if (col.ref) return lookups?.[col.ref]?.[row[col.key]] || "";
  if (col.render) return String(col.render(row) ?? "");
  return row[col.key];
}

function Cell({ col, row, lookups }) {
  const v = row[col.key];
  if (col.render) return col.render(row, lookups);
  if (col.ref) return lookups?.[col.ref]?.[v] || <span className="text-muted-foreground">—</span>;
  if (col.badge && v) return <Pill value={v}>{labelOf(col.options || [], v)}</Pill>;
  if (col.options) return labelOf(col.options, v);
  if (col.date) return <span className="whitespace-nowrap text-muted-foreground">{fmtDate(v)}</span>;
  if (col.money) return <span className="font-mono">{fmtMoney(v, row.currency || "GBP")}</span>;
  if (col.score) return v == null ? <span className="text-muted-foreground">—</span> : <span className="font-mono text-primary">{v}</span>;
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  return <span className={`${col.mono ? "font-mono text-xs" : ""} ${col.truncate ? "line-clamp-1 max-w-xs" : ""}`}>{v}</span>;
}

export function DataTable({ columns, rows, loading, error, onRetry, onRowClick, rowActions, lookups, empty, testid = "data-table", selected, onSelect }) {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const sorted = useMemo(() => {
    if (!rows || !sort.key) return rows || [];
    const col = columns.find((c) => c.key === sort.key);
    return [...rows].sort((a, b) => {
      const x = rawValue(col, a, lookups) ?? "", y = rawValue(col, b, lookups) ?? "";
      const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns, lookups]);

  if (error) return <ErrorState message={error} onRetry={onRetry} testid={`${testid}-error`} />;
  if (loading && !rows) return <div className="space-y-2 p-4" data-testid={`${testid}-loading`}>{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>;
  if (!sorted.length) return empty || <EmptyState title="No records yet" testid={`${testid}-empty`} />;

  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const allOn = onSelect && sorted.every((r) => selected.includes(r.id));
  const flip = (id) => onSelect(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            {onSelect && <th className="w-10 pl-4"><input type="checkbox" aria-label="Select all shown" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={allOn}
              onChange={() => onSelect(allOn ? [] : sorted.map((r) => r.id))} data-testid={`${testid}-select-all`} /></th>}
            {columns.map((c) => (
              <th key={c.key} className={`px-4 py-3 text-left font-normal ${c.align === "right" ? "text-right" : ""}`}>
                <button type="button" onClick={() => toggle(c.key)} data-testid={`${testid}-sort-${c.key}`}
                  className="eyebrow inline-flex items-center gap-1 transition-colors hover:text-foreground">
                  {c.label}
                  {sort.key === c.key ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                </button>
              </th>
            ))}
            {rowActions && <th className="w-10" />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id} data-testid={`${testid}-row-${row.id}`} onClick={() => onRowClick?.(row)}
              className={`border-b border-border/60 transition-colors hover:bg-accent/50 ${onRowClick ? "cursor-pointer" : ""} ${selected?.includes(row.id) ? "bg-accent/40" : ""}`}>
              {onSelect && <td className="pl-4" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Select row" className="h-4 w-4 accent-[hsl(var(--primary))]"
                checked={selected.includes(row.id)} onChange={() => flip(row.id)} data-testid={`${testid}-select-${row.id}`} /></td>}
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-3 align-middle ${c.strong ? "font-medium" : ""} ${c.align === "right" ? "text-right" : ""}`}>
                  <Cell col={c} row={row} lookups={lookups} />
                </td>
              ))}
              {rowActions && (
                <td className="px-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="rounded-md p-1.5 hover:bg-accent" data-testid={`${testid}-row-actions-${row.id}`}><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {rowActions(row).map((a) => (
                        <DropdownMenuItem key={a.label} onClick={a.onClick} data-testid={`${testid}-action-${a.id}-${row.id}`} className={a.danger ? "text-destructive" : ""}>{a.label}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
